import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faInbox,
    faTruck,
    faCalendarAlt,
    faDna, // A good fallback icon
    faBoxOpen
} from '@fortawesome/free-solid-svg-icons';
import SampleDetail from './SampleDetail'; // This component will receive an "order" object
import './Calendar.css';

const Calendar = ({ onDateSelect, onSearch, dateFilter, searchTerm, refreshTrigger, onRefreshData }) => {
    // Note: The "samples" state now holds transformed ORDER objects. We keep the name to minimize refactoring of the rendering logic.
    const [samples, setSamples] = useState([]);
    const [filteredSamples, setFilteredSamples] = useState([]);
    const [groupedSamples, setGroupedSamples] = useState([]);
    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || '');
    const [selectedDate, setSelectedDate] = useState(dateFilter);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [selectedSample, setSelectedSample] = useState(null); // This will hold the selected ORDER
    const [showDetailCard, setShowDetailCard] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const calendarRef = useRef(null);
    const todayRef = useRef(null);
    
    // Simplified status icons for Orders
    const statusIcons = {
        'Order Received': faInbox,
        'Sent Cargo': faTruck,
        'Cargo Delivered': faBoxOpen, // New icon
    };
    // Fetch orders data function
    const fetchOrders = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetch('https://service9004.szapfs.org/api/orders');
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Transform the raw order data into the "sample" shape the calendar's rendering logic expects
            const transformedData = data.map(order => {
                // MODIFIED: Updated currentStage logic
                let currentStage = "Order Received";
                if (order.sent_cargo) currentStage = "Sent Cargo";
                if (order.cargo_delivered) currentStage = "Cargo Delivered";

                return {
                    id: order.OrderID, 
                    firstName: order.firstName,
                    lastName: order.lastName,
                    email: order.Email,
                    kitType: order.Items.length > 1 
                        ? `${order.Items.length} Kits` 
                        : (order.Items[0]?.KitName || '1 Kit'),
                    items: order.Items,
                    address: order.address, // Pass new fields through
                    outbound_url: order.outbound_url,
                    
                    status: {
                        currentStage: currentStage,
                        stages: {
                            "Order Received": order.order_received,
                            "Sent Cargo": order.sent_cargo,
                            "Cargo Delivered": order.cargo_delivered, // New stage
                        }
                    },
                };
            });
            
            setSamples(transformedData);
            setFilteredSamples(transformedData);
            setError(null);
            console.log(transformedData);

        } catch (err) {
            console.error("Error fetching orders for calendar:", err);
            setError("Failed to load orders. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initial data fetch and refresh triggers
    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);
    
    useEffect(() => {
        if (refreshTrigger > 0) {
            console.log('Calendar refreshing data due to trigger:', refreshTrigger);
            fetchOrders();
        }
    }, [refreshTrigger, fetchOrders]);

    // Scroll to today when component mounts or when zoom level changes
    useEffect(() => {
        if (!todayRef.current || !calendarRef.current) return;
        const container = calendarRef.current;
        const dayEl = todayRef.current;
        
        const dayCenter = dayEl.offsetLeft + (dayEl.offsetWidth / 2);
        const newScrollLeft = (dayCenter * zoomLevel) - (container.clientWidth / 2);
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        container.scrollLeft = Math.min(Math.max(newScrollLeft, 0), maxScrollLeft);
    }, [zoomLevel]);

    // Filter orders based on search term
    useEffect(() => {
        if (localSearchTerm.trim() === '') {
            setFilteredSamples(samples);
        } else {
            const filtered = samples.filter(sample => { // "sample" here is a transformed order
                const searchTermLower = localSearchTerm.toLowerCase();
                return (
                    (sample.id && sample.id.toLowerCase().includes(searchTermLower)) ||
                    (sample.firstName && sample.firstName.toLowerCase().includes(searchTermLower)) ||
                    (sample.lastName && sample.lastName.toLowerCase().includes(searchTermLower)) ||
                    (sample.email && sample.email.toLowerCase().includes(searchTermLower))
                );
            });
            setFilteredSamples(filtered);
        }
    }, [localSearchTerm, samples]);

    // Group samples into rows to avoid collisions (this logic remains unchanged)
    useEffect(() => {
        const days = generateDays();
        const samplesWithBars = filteredSamples
            .map(sample => {
                const barData = calculateSampleBar(sample);
                if (!barData) return null;

                const startIndex = days.findIndex(day => new Date(day).setHours(0,0,0,0) === barData.startDate.getTime());
                
                // Calculate endIndex directly from the latest stage date, not from barData.endDate
                let endIndex = startIndex; // Default to start if no other stages
                
                // Find the latest stage date and use that for the end
                Object.values(barData.stagesDates).forEach(stageDate => {
                    if (stageDate) {
                        const stageDateNormalized = new Date(stageDate).setHours(0, 0, 0, 0);
                        const stageDayIndex = days.findIndex(day => 
                            new Date(day).setHours(0, 0, 0, 0) === stageDateNormalized
                        );
                        if (stageDayIndex > endIndex) {
                            endIndex = stageDayIndex;
                        }
                    }
                });
                
                if (startIndex === -1) return null;
                
                // Pre-calculate stage marker positions using the same days array
                const stageMarkers = [];
                Object.entries(barData.stagesDates).forEach(([stageName, stageDate]) => {
                    if (stageDate && stageName !== 'Order Received') {
                        const stageDateNormalized = new Date(stageDate).setHours(0, 0, 0, 0);
                        const stageDayIndex = days.findIndex(day => 
                            new Date(day).setHours(0, 0, 0, 0) === stageDateNormalized
                        );
                        
                        if (stageDayIndex !== -1) {
                            const barDuration = endIndex - startIndex + 1;
                            const relativePosition = stageDayIndex - startIndex + 0.5;
                            let positionPercent = (relativePosition / barDuration) * 100;
                            positionPercent = Math.max(10, Math.min(95, positionPercent));
                            
                            stageMarkers.push({
                                stageName,
                                stageDate,
                                positionPercent
                            });
                        }
                    }
                });
                
                return { sample, startIndex, endIndex, barData, stageMarkers };
            })
            .filter(Boolean);

        samplesWithBars.sort((a, b) => a.startIndex - b.startIndex || (a.endIndex - a.startIndex) - (b.endIndex - b.startIndex));

        const rows = [];
        samplesWithBars.forEach(sampleWithBar => {
            let rowIndex = 0;
            let placed = false;
            while (!placed) {
                if (!rows[rowIndex]) {
                    rows[rowIndex] = [sampleWithBar];
                    placed = true;
                } else {
                    const overlaps = rows[rowIndex].some(existing => (sampleWithBar.startIndex <= existing.endIndex) && (sampleWithBar.endIndex >= existing.startIndex));
                    if (!overlaps) {
                        rows[rowIndex].push(sampleWithBar);
                        placed = true;
                    } else {
                        rowIndex++;
                    }
                }
            }
        });
        
        setGroupedSamples(rows);
    }, [filteredSamples]);

    const getDateRange = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1, today.getDate() - 15);
        const endDate = new Date(today);
        endDate.setMonth(today.getMonth() + 1, today.getDate() + 15);
        return { startDate, endDate };
    };

    const generateDays = useCallback(() => {
        const { startDate, endDate } = getDateRange();
        const days = [];
        let currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            days.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }
        return days;
    }, []);

    const isToday = (date) => {
        const today = new Date();
        return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    };

    // This function works correctly with the new simplified 2-stage data
    const calculateSampleBar = (sample) => {
        const stages = sample.status.stages;
        let startDate = stages['Order Received'] ? new Date(stages['Order Received']) : null;
        // The bar should now end at the 'Cargo Delivered' date if it exists
        let endDate = stages['Cargo Delivered'] ? new Date(stages['Cargo Delivered']) : (stages['Sent Cargo'] ? new Date(stages['Sent Cargo']) : startDate);

        if (!startDate) return null;

        startDate.setHours(0, 0, 0, 0);
        if (endDate) endDate.setHours(0, 0, 0, 0);
        
        // Prepare stage dates for markers
        const stagesDates = { 
            'Order Received': startDate, 
            'Sent Cargo': stages['Sent Cargo'] ? new Date(stages['Sent Cargo']) : null,
            'Cargo Delivered': stages['Cargo Delivered'] ? new Date(stages['Cargo Delivered']) : null
        };

        return { startDate, endDate, stagesDates };
    };

    const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.2, 2));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
    const handleSampleClick = (e, order) => { setSelectedSample(order); setShowDetailCard(true); };
    const handleCloseDetailCard = () => { setShowDetailCard(false); setSelectedSample(null); };
    const getCurrentMonthYear = () => new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
    const handleSearchChange = (e) => { setLocalSearchTerm(e.target.value); if (onSearch) onSearch(e.target.value); };

    const handleDayClick = (date) => {
        const normalizedClickedDate = new Date(date).setHours(0, 0, 0, 0);
        const currentSelectedTime = selectedDate ? new Date(selectedDate).setHours(0, 0, 0, 0) : null;
        const newSelectedDate = normalizedClickedDate === currentSelectedTime ? null : date;
        setSelectedDate(newSelectedDate);
        if (onDateSelect) onDateSelect(newSelectedDate);
    };

    return (
        <div className="samplemanager-calendar-container">
            <div className="samplemanager-calendar-container-top">
                <div className="samplemanager-calendar-header-left">
                    <FontAwesomeIcon icon={faCalendarAlt} className="samplemanager-calendar-icon" />
                    <span className="samplemanager-calendar-month">{getCurrentMonthYear()}</span>
                </div>
                <div className="samplemanager-calendar-header-right">
                    <div className="samplemanager-calendar-search">
                        <FontAwesomeIcon icon={faSearch} className="samplemanager-search-icon" />
                        <input 
                            type="text"
                            value={localSearchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search orders, names, emails..."
                            className="samplemanager-search-input"
                        />
                    </div>
                </div>
            </div>
            <div className="samplemanager-calendar-container-bottom" ref={calendarRef}>
                <div 
                    className="samplemanager-calendar-grid" 
                    style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'left top' }}
                >
                    <div className="samplemanager-calendar-days">
                        {generateDays().map((day, index) => {
                            const dayIsToday = isToday(day);
                            const isSelected = selectedDate && new Date(selectedDate).setHours(0, 0, 0, 0) === new Date(day).setHours(0, 0, 0, 0);
                            return (
                                <div 
                                    key={index} 
                                    className={`samplemanager-calendar-day ${dayIsToday ? 'samplemanager-today' : ''} ${isSelected ? 'selected' : ''}`}
                                    ref={dayIsToday ? todayRef : null}
                                    onClick={() => handleDayClick(day)}
                                >
                                    <div className="samplemanager-day-header">
                                        <span className="samplemanager-day-name">{day.toLocaleString('default', { weekday: 'short' })}</span>
                                        <span className="samplemanager-day-number">{day.getDate()}</span>
                                    </div>
                                    <div className="samplemanager-day-vertical-line-right"></div>
                                    {dayIsToday && <div className="samplemanager-today-vertical-line"></div>}
                                </div>
                            );
                        })}
                    </div>
                    <div className="samplemanager-calendar-samples">
                        {groupedSamples.map((row, rowIndex) => (
                            <div 
                                key={`row-${rowIndex}`} 
                                className="samplemanager-sample-row"
                                style={{ top: `${rowIndex * 3}vh` }}
                            >
                                {row.map(({ sample, startIndex, endIndex, barData, stageMarkers }) => {
                                    const dayWidth = 10;
                                    const barWidth = (endIndex - startIndex + 1) * dayWidth;
                                    const barLeft = startIndex * dayWidth;

                                    return (
                                        <div 
                                            key={sample.id}
                                            className="samplemanager-sample-bar"
                                            style={{ left: `${barLeft}vh`, width: `${barWidth}vh`, height: '2vh', top: '0.5vh' }}
                                            onClick={(e) => handleSampleClick(e, sample)}
                                        >
                                            {/* Red bouncy indicator for "Order Received" status */}
                                            {sample.status.currentStage === 'Order Received' && (
                                                <div className="samplemanager-status-indicator">
                                                    <div className="samplemanager-bouncy-dot">!</div>
                                                </div>
                                            )}
                                            
                                            <div className="samplemanager-sample-info">
                                                <div className="samplemanager-kit-count">
                                                    {sample.items.length}
                                                </div>
                                                <span className="samplemanager-sample-name">{sample.firstName} {sample.lastName}</span>
                                            </div>
                                            
                                            {/* Stage markers using pre-calculated positions */}
                                            {stageMarkers.map((marker) => (
                                                <div
                                                    key={marker.stageName}
                                                    className="samplemanager-stage-marker"
                                                    style={{ left: `${marker.positionPercent}%` }}
                                                    title={`${marker.stageName}: ${marker.stageDate.toLocaleDateString()}`}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={statusIcons[marker.stageName]}
                                                        className="samplemanager-stage-icon"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            
            {showDetailCard && selectedSample && (
                <SampleDetail 
                    order={selectedSample} // Pass prop as "order"
                    onClose={handleCloseDetailCard}
                    onSaveChanges={onRefreshData}
                />
            )}
        </div>
    );
};

export default Calendar;